import React, { useState, useEffect, useCallback } from "react";
import { filterTracking, filterRechargeTracking, clearTrackingRecords, clearRechargeTracking } from "../api/userApi";
import { filterCoinLogs, createCoinClearance } from "../api/coinLogsApi";
import { useTranslation } from "react-i18next";
import { 
  Calendar, Search, FileText, RefreshCw, Download, 
  CreditCard, Coins, Filter, ChevronDown, Loader2,
  CheckCircle, Clock, AlertCircle, User,
  DollarSign, ArrowDownUp, Gamepad2, HardDrive
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

export default function ReportingDashboard() {
  const { userData } = useAuth();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("cards");
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    isCleared: "",
    mid_id: userData.id
  });
  const [cardData, setCardData] = useState([]);
  const [rechargeData, setRechargeData] = useState([]);
  const [tokenData, setTokenData] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [isClearing, setIsClearing] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    key: "createdAt",
    direction: "desc"
  });

  // Calculate summary statistics for cards tab
  const cardSummary = React.useMemo(() => {
    if (!cardData.length) return { total: 0, new: 0, replaced: 0, cleared: 0, pending: 0 };
    
    return cardData.reduce((acc, item) => {
      acc.total++;
      if (item.action === "new") acc.new++;
      if (item.action === "replaced") acc.replaced++;
      if (item.isCleared) acc.cleared++;
      if (!item.isCleared) acc.pending++;
      return acc;
    }, { total: 0, new: 0, replaced: 0, cleared: 0, pending: 0 });
  }, [cardData]);

  // Calculate summary statistics for recharge tab
  const rechargeSummary = React.useMemo(() => {
    if (!rechargeData.length) return { 
      total: 0, 
      totalTokens: 0, 
      totalAmount: 0, 
      cleared: 0, 
      pending: 0 
    };
    
    return rechargeData.reduce((acc, item) => {
      acc.total++;
      acc.totalTokens += parseInt(item.tokens || 0, 10);
      acc.totalAmount += parseFloat(item.paidAmount || 0);
      if (item.isCleared) acc.cleared++;
      if (!item.isCleared) acc.pending++;
      return acc;
    }, { total: 0, totalTokens: 0, totalAmount: 0, cleared: 0, pending: 0 });
  }, [rechargeData]);
  
  // Calculate summary statistics for token tracking tab
  const tokenSummary = React.useMemo(() => {
    if (!tokenData.length) return {
      totalLogs: 0,
      totalTokens: 0,
      uniqueMachines: 0,
      uniquePlayers: 0,
      cleared: 0,
      pending: 0
    };
    
    const uniqueMachineIds = new Set();
    const uniquePlayerIds = new Set();
    
    const summary = tokenData.reduce((acc, item) => {
      acc.totalLogs++;
      acc.totalTokens += parseInt(item.tokens || 0, 10);
      
      if (item.machineId) uniqueMachineIds.add(item.machineId);
      if (item.player_id) uniquePlayerIds.add(item.player_id);
      
      if (item.isCleared) acc.cleared++;
      if (!item.isCleared) acc.pending++;
      
      return acc;
    }, { totalLogs: 0, totalTokens: 0, cleared: 0, pending: 0 });
    
    summary.uniqueMachines = uniqueMachineIds.size;
    summary.uniquePlayers = uniquePlayerIds.size;
    
    return summary;
  }, [tokenData]);

  // Sort data
  const sortedData = React.useMemo(() => {
    let dataToSort;
    
    if (activeTab === "cards") {
      dataToSort = [...cardData];
    } else if (activeTab === "recharge") {
      dataToSort = [...rechargeData];
    } else {
      dataToSort = [...tokenData];
    }
    
    if (sortConfig.key) {
      dataToSort.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];
        
        // Handle specific data types
        if (sortConfig.key === "createdAt") {
          aValue = new Date(aValue);
          bValue = new Date(bValue);
        } else if (sortConfig.key === "tokens" || sortConfig.key === "paidAmount") {
          aValue = parseFloat(aValue || 0);
          bValue = parseFloat(bValue || 0);
        } else if (
          sortConfig.key === "machine" && 
          activeTab === "tokens" && 
          a.machine && b.machine
        ) {
          aValue = a.machine.type || "";
          bValue = b.machine.type || "";
        } else if (typeof aValue === "string" && typeof bValue === "string") {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }
        
        if (aValue < bValue) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }
    
    return dataToSort;
  }, [activeTab, cardData, rechargeData, tokenData, sortConfig]);

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction: sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc"
    });
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  // Reset filters
  const resetFilters = () => {
    setFilters({
      startDate: "",
      endDate: "",
      isCleared: "",
      mid_id: userData.id
    });
    setSelectedRows([]);
  };

  const getRowId = (item, index) => item?.id ?? item?._id ?? `${activeTab}-${index}`;
  const isRowSelected = (rowId) => selectedRows.includes(rowId);
  const getSelectedItems = () => sortedData.filter((item, index) => selectedRows.includes(getRowId(item, index)));

  const toggleRowSelection = (rowId) => {
    setSelectedRows((prev) =>
      prev.includes(rowId) ? prev.filter((id) => id !== rowId) : [...prev, rowId]
    );
  };

  const toggleSelectAll = () => {
    if (!sortedData.length) return;
    const ids = sortedData.map((item, index) => getRowId(item, index));
    const allSelected = ids.every((id) => selectedRows.includes(id));
    setSelectedRows(allSelected ? [] : ids);
  };

  const clearSelected = async () => {
    const selectedItems = getSelectedItems();
    if (!selectedItems.length) return;

    setIsClearing(true);
    try {
      const ids = selectedItems
        .map((item) => item?.id ?? item?._id)
        .filter((id) => id != null);

      if (!ids.length) throw new Error('No valid selected ids to clear.');

      if (activeTab === "cards") {
        await clearTrackingRecords(ids);
      } else if (activeTab === "recharge") {
        await clearRechargeTracking(ids);
      } else if (activeTab === "tokens") {
        const clearPromises = selectedItems.map((item) => {
          const id = item.id ?? item?._id;
          if (!id) return Promise.resolve();
          return createCoinClearance(id, { isCleared: true });
        });

        await Promise.all(clearPromises);
      }

      setSelectedRows([]);
      await fetchData();
      alert(t('selectedRecordsCleared', 'Selected records cleared successfully.'));
    } catch (error) {
      console.error('Error clearing selected records:', error);
      alert(t('clearError', 'Failed to clear selected records.'));
    } finally {
      setIsClearing(false);
    }
  };

  const printReceipt = () => {
    const selectedItems = getSelectedItems();
    if (!selectedItems.length) return;

    const title =
      activeTab === "cards"
        ? t('cardClearanceReceipt', 'Card Clearance Receipt')
        : activeTab === "recharge"
        ? t('rechargeReceipt', 'Recharge Receipt')
        : t('coinClearanceReceipt', 'Coin Clearance Receipt');

    const rowsHtml = selectedItems
      .map((item, index) => {
        const date = formatDate(item.createdAt);
        const status = item.isCleared ? t('cleared') : t('pending');
        if (activeTab === "cards") {
          return `
            <tr>
              <td>${index + 1}</td>
              <td>${date}</td>
              <td>${item.users?.user_name || item.users?.full_name || 'N/A'}</td>
              <td>${item.mediators?.user_name || item.mediators?.full_name || 'N/A'}</td>
              <td>${item.action || 'N/A'}</td>
              <td>${status}</td>
            </tr>
          `;
        }

        if (activeTab === "recharge") {
          return `
            <tr>
              <td>${index + 1}</td>
              <td>${date}</td>
              <td>${item.users?.user_name || item.users?.full_name || 'N/A'}</td>
              <td>${item.tokens || 0}</td>
              <td>${parseFloat(item.paidAmount || 0).toFixed(2)}</td>
              <td>${status}</td>
            </tr>
          `;
        }

        return `
          <tr>
            <td>${index + 1}</td>
            <td>${date}</td>
            <td>${item.player?.user_name || item.player?.full_name || 'N/A'}</td>
            <td>${item.machine?.type || 'N/A'}</td>
            <td>${item.tokens || 0}</td>
            <td>${status}</td>
          </tr>
        `;
      })
      .join('');

    const receiptHtml = `
      <html>
      <head>
        <title>${title}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 24px; }
          h1 { font-size: 20px; margin-bottom: 16px; }
          table { width: 100%; border-collapse: collapse; margin-top: 16px; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background: #f7f7f7; }
          .summary { margin-top: 16px; }
        </style>
      </head>
      <body>
        <h1>${title}</h1>
        <p>${t('printDate', 'Printed on')}: ${formatDate(new Date().toISOString())}</p>
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>${t('date')}</th>
              <th>${activeTab === 'tokens' ? t('player') : t('user')}</th>
              <th>${activeTab === 'tokens' ? t('machineType') : activeTab === 'cards' ? t('admin') : t('tokens')}</th>
              <th>${activeTab === 'cards' ? t('action') : activeTab === 'recharge' ? t('amount') : t('tokens')}</th>
              <th>${t('status')}</th>
            </tr>
          </thead>
          <tbody>
            ${rowsHtml}
          </tbody>
        </table>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(receiptHtml);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
    }
  };

  // Fetch data based on active tab and filters
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      // Only include non-empty filters
      const filterParams = Object.entries(filters).reduce((acc, [key, value]) => {
        if (value !== "") acc[key] = value;
        return acc;
      }, {});
      
      if (activeTab === "cards") {
        const data = await filterTracking(filterParams);
        setCardData(data);
      } else if (activeTab === "recharge") {
        const data = await filterRechargeTracking(filterParams);
        setRechargeData(data);
      } else if (activeTab === "tokens") {
        const data = await filterCoinLogs(filterParams);
        setTokenData(data);
      }

    } catch (error) {
      console.error(`Error fetching ${activeTab} data:`, error);
    } finally {
      setIsLoading(false);
    }
  }, [activeTab, filters]);

  // Initial data fetch
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // When tab changes, refresh data and clear selections
  useEffect(() => {
    setSelectedRows([]);
    fetchData();
  }, [activeTab, fetchData]);

  // Export data to CSV
  const exportToCSV = () => {
    let data;
    if (activeTab === "cards") {
      data = cardData;
    } else if (activeTab === "recharge") {
      data = rechargeData;
    } else {
      data = tokenData;
    }
    
    if (!data.length) return;
    
    let csvContent = "";
    // Headers
    let headers;
    
    if (activeTab === "tokens") {
      headers = ["createdAt", "tokens", "isCleared", "isSynced"];
    } else {
      headers = Object.keys(data[0]).filter(key => 
        key !== "users" && key !== "mediators" && key !== "player" && 
        key !== "mediator" && key !== "machine" && !key.includes("_id")
      );
    }
    
    // Add custom headers based on tab
    if (activeTab === "tokens") {
      csvContent += "Date,Player,Machine Type,Tokens,Status,Synced\n";
      
      // Rows for token data
      data.forEach(item => {
        const playerName = item.player?.user_name || "N/A";
        const machineType = item.machine?.type || "N/A";
        const date = formatDate(item.createdAt);
        const tokens = item.tokens || 0;
        const status = item.isCleared ? t('cleared') : t('pending');
        const synced = item.isSynced ? t('yes') : t('no');
        
        csvContent += `"${t('date')}","${t('player')}","${t('machineType')}","${t('tokens')}","${t('status')}","${t('synced')}"\n`;
        csvContent += `"${date}","${playerName}","${machineType}",${tokens},"${status}","${synced}"\n`;
      });
    } else {
      // For cards and recharge
      csvContent += "User,Admin,";
      csvContent += headers.join(",") + "\n";
      
      // Rows
      data.forEach(item => {
        const userName = item.users?.user_name || item.users?.full_name || "N/A";
        const adminName = item.mediators?.full_name || "N/A";
        
        csvContent += `"${userName}","${adminName}",`;
        
        headers.forEach((key, index) => {
          let value = item[key];
          
          // Format specific fields
          if (key === "createdAt") {
            value = formatDate(value);
          } else if (key === "isCleared") {
            value = value ? "Yes" : "No";
          }
          
          if (typeof value === "string" && value.includes(",")) {
            csvContent += `"${value}"`;
          } else {
            csvContent += value;
          }
          
          if (index < headers.length - 1) {
            csvContent += ",";
          }
        });
        
        csvContent += "\n";
      });
    }
    
    // Create download link
    const encodedUri = encodeURI("data:text/csv;charset=utf-8," + csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `${activeTab}_report_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Render status badge
  const StatusBadge = ({ isCleared }) => (
    <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${
      isCleared 
        ? "bg-green-100 text-green-800" 
        : "bg-amber-100 text-amber-800"
    }`}>
      {isCleared ? (
        <><CheckCircle size={12} /> {t('cleared')}</>
      ) : (
        <><Clock size={12} /> {t('pending')}</>
      )}
    </span>
  );

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6 mb-9">
      <h1 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
        <FileText size={24} className="mr-2" />
        {t('reportingDashboard')}
      </h1>
      
      {/* Tabs */}
      <div className="flex mb-6 border-b overflow-x-auto">
        <button
          onClick={() => setActiveTab("cards")}
          className={`flex items-center gap-1 px-4 py-3 font-medium text-sm whitespace-nowrap ${
            activeTab === "cards"
              ? "text-blue-600 border-b-2 border-blue-600"
              : "text-gray-600 hover:text-gray-800"
          }`}
        >
          <CreditCard size={16} />
          {t('cardTracking')}
        </button>
        <button
          onClick={() => setActiveTab("recharge")}
          className={`flex items-center gap-1 px-4 py-3 font-medium text-sm whitespace-nowrap ${
            activeTab === "recharge"
              ? "text-blue-600 border-b-2 border-blue-600"
              : "text-gray-600 hover:text-gray-800"
          }`}
        >
          <Coins size={16} />
          {t('rechargeTracking')}
        </button>
        <button
          onClick={() => setActiveTab("tokens")}
          className={`flex items-center gap-1 px-4 py-3 font-medium text-sm whitespace-nowrap ${
            activeTab === "tokens"
              ? "text-blue-600 border-b-2 border-blue-600"
              : "text-gray-600 hover:text-gray-800"
          }`}
        >
          <Gamepad2 size={16} />
          {t('tokenTracking')}
        </button>
      </div>
      
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">
        {activeTab === "cards" && (
          <>
            <div className="bg-white rounded-lg shadow p-4 border-l-4 border-blue-500">
              <div className="flex justify-between">
                <div>
                  <p className="text-sm text-gray-500">{t('totalCards')}</p>
                  <h3 className="text-2xl font-bold">{cardSummary.total}</h3>
                </div>
                <div className="bg-blue-100 p-2 rounded-lg">
                  <CreditCard size={24} className="text-blue-600" />
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4 border-l-4 border-emerald-500">
              <div className="flex justify-between">
                <div>
                  <p className="text-sm text-gray-500">{t('newCards')}</p>
                  <h3 className="text-2xl font-bold">{cardSummary.new}</h3>
                </div>
                <div className="bg-emerald-100 p-2 rounded-lg">
                  <CreditCard size={24} className="text-emerald-600" />
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4 border-l-4 border-amber-500">
              <div className="flex justify-between">
                <div>
                  <p className="text-sm text-gray-500">{t('replacedCards')}</p>
                  <h3 className="text-2xl font-bold">{cardSummary.replaced}</h3>
                </div>
                <div className="bg-amber-100 p-2 rounded-lg">
                  <RefreshCw size={24} className="text-amber-600" />
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4 border-l-4 border-green-500">
              <div className="flex justify-between">
                <div>
                  <p className="text-sm text-gray-500">{t('clearanceStatus')}</p>
                  <div className="flex items-center gap-2">
                    <span className="text-green-600 font-bold">{cardSummary.cleared}</span>
                    <span className="text-gray-400">/</span>
                    <span className="text-amber-600 font-bold">{cardSummary.pending}</span>
                  </div>
                </div>
                <div className="bg-green-100 p-2 rounded-lg">
                  <CheckCircle size={24} className="text-green-600" />
                </div>
              </div>
            </div>
          </>
        )}
        
        {activeTab === "recharge" && (
          <>
            <div className="bg-white rounded-lg shadow p-4 border-l-4 border-purple-500">
              <div className="flex justify-between">
                <div>
                  <p className="text-sm text-gray-500">{t('totalRecharges')}</p>
                  <h3 className="text-2xl font-bold">{rechargeSummary.total}</h3>
                </div>
                <div className="bg-purple-100 p-2 rounded-lg">
                  <Coins size={24} className="text-purple-600" />
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4 border-l-4 border-blue-500">
              <div className="flex justify-between">
                <div>
                  <p className="text-sm text-gray-500">{t('totalTokens')}</p>
                  <h3 className="text-2xl font-bold">{rechargeSummary.totalTokens}</h3>
                </div>
                <div className="bg-blue-100 p-2 rounded-lg">
                  <Coins size={24} className="text-blue-600" />
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4 border-l-4 border-green-500">
              <div className="flex justify-between">
                <div>
                  <p className="text-sm text-gray-500">{t('totalAmount')}</p>
                  <h3 className="text-2xl font-bold flex items-center">
                    <DollarSign size={18} />
                    {rechargeSummary.totalAmount.toFixed(2)}
                  </h3>
                </div>
                <div className="bg-green-100 p-2 rounded-lg">
                  <DollarSign size={24} className="text-green-600" />
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4 border-l-4 border-amber-500">
              <div className="flex justify-between">
                <div>
                  <p className="text-sm text-gray-500">{t('clearanceStatus')}</p>
                  <div className="flex items-center gap-2">
                    <span className="text-green-600 font-bold">{rechargeSummary.cleared}</span>
                    <span className="text-gray-400">/</span>
                    <span className="text-amber-600 font-bold">{rechargeSummary.pending}</span>
                  </div>
                </div>
                <div className="bg-amber-100 p-2 rounded-lg">
                  <CheckCircle size={24} className="text-amber-600" />
                </div>
              </div>
            </div>
          </>
        )}
        
        {activeTab === "tokens" && (
          <>
            <div className="bg-white rounded-lg shadow p-4 border-l-4 border-indigo-500">
              <div className="flex justify-between">
                <div>
                  <p className="text-sm text-gray-500">{t('totalTransactions')}</p>
                  <h3 className="text-2xl font-bold">{tokenSummary.totalLogs}</h3>
                </div>
                <div className="bg-indigo-100 p-2 rounded-lg">
                  <Gamepad2 size={24} className="text-indigo-600" />
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4 border-l-4 border-blue-500">
              <div className="flex justify-between">
                <div>
                  <p className="text-sm text-gray-500">{t('totalTokensSpent')}</p>
                  <h3 className="text-2xl font-bold">{tokenSummary.totalTokens}</h3>
                </div>
                <div className="bg-blue-100 p-2 rounded-lg">
                  <Coins size={24} className="text-blue-600" />
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4 border-l-4 border-teal-500">
              <div className="flex justify-between">
                <div>
                  <p className="text-sm text-gray-500">{t('machines')}</p>
                  <h3 className="text-2xl font-bold">{tokenSummary.uniqueMachines}</h3>
                </div>
                <div className="bg-teal-100 p-2 rounded-lg">
                  <HardDrive size={24} className="text-teal-600" />
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4 border-l-4 border-orange-500">
              <div className="flex justify-between">
                <div>
                  <p className="text-sm text-gray-500">{t('players')}</p>
                  <h3 className="text-2xl font-bold">{tokenSummary.uniquePlayers}</h3>
                </div>
                <div className="bg-orange-100 p-2 rounded-lg">
                  <User size={24} className="text-orange-600" />
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      
      {/* Filter Panel */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-4 border-b flex justify-between items-center">
          <h2 className="font-semibold flex items-center gap-1">
            <Filter size={16} />
            {t('filters')}
          </h2>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="text-sm text-blue-600 flex items-center gap-1"
          >
            {showFilters ? t('hideFilters') : t('showFilters')}
            <ChevronDown size={16} className={`transform transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </button>
        </div>
        
        {showFilters && (
          <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('startDate')}</label>
              <div className="relative">
                <input
                  type="date"
                  name="startDate"
                  value={filters.startDate}
                  onChange={handleFilterChange}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <Calendar size={16} className="absolute right-3 top-2.5 text-gray-400" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('endDate')}</label>
              <div className="relative">
                <input
                  type="date"
                  name="endDate"
                  value={filters.endDate}
                  onChange={handleFilterChange}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <Calendar size={16} className="absolute right-3 top-2.5 text-gray-400" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('clearanceStatus')}</label>
              <select
                name="isCleared"
                value={filters.isCleared}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">{t('all')}</option>
                <option value="true">{t('cleared')}</option>
                <option value="false">{t('pending')}</option>
              </select>
            </div>

            <div className="md:col-span-2 lg:col-span-4 flex justify-end gap-2">
              <button
                onClick={resetFilters}
                className="px-3 py-2 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50"
              >
                {t('reset')}
              </button>
              <button
                onClick={fetchData}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-1"
              >
                <Search size={16} />
                {t('apply')}
              </button>
            </div>
          </div>
        )}
      </div>
      
      {/* Data Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="font-semibold text-lg">
            {activeTab === "cards" 
              ? t('cardTrackingRecords') 
              : activeTab === "recharge" 
                ? t('rechargeTrackingRecords')
                : t('tokenTrackingRecords')
            }
          </h2>
          <div className="flex gap-2 items-center">
            <button
              onClick={toggleSelectAll}
              disabled={!sortedData.length}
              className="px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 disabled:opacity-50"
            >
              {selectedRows.length === sortedData.length && sortedData.length > 0
                ? t('deselectAll', 'Deselect All')
                : t('selectAll', 'Select All')}
            </button>
            <button
              onClick={clearSelected}
              disabled={!selectedRows.length || isClearing}
              className="px-3 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600 disabled:opacity-50"
            >
              {isClearing ? t('clearing', 'Clearing...') : t('clearSelected', 'Clear Selected')}
            </button>
            <button
              onClick={printReceipt}
              disabled={!selectedRows.length}
              className="px-3 py-2 bg-indigo-500 text-white rounded-md hover:bg-indigo-600 disabled:opacity-50"
            >
              {t('printReceipt', 'Print Receipt')}
            </button>
            <button
              onClick={fetchData}
              className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-md"
              title={t('refresh')}
            >
              <RefreshCw size={18} />
            </button>
            <button
              onClick={exportToCSV}
              disabled={
                (activeTab === "cards" && !cardData.length) || 
                (activeTab === "recharge" && !rechargeData.length) ||
                (activeTab === "tokens" && !tokenData.length)
              }
              className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-md disabled:text-gray-300 disabled:hover:bg-transparent"
              title={t('exportToCsv')}
            >
              <Download size={18} />
            </button>
          </div>
        </div>
        
        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 size={32} className="animate-spin text-blue-600" />
          </div>
        ) : sortedData.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <AlertCircle size={48} className="text-gray-300 mb-3" />
            <h3 className="text-lg font-medium text-gray-700 mb-1">{t('noRecordsFound')}</h3>
            <p className="text-gray-500 max-w-md">
              {t('noRecordsFoundDescription')}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {/* Common columns for both tabs */}
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <input
                      type="checkbox"
                      checked={sortedData.length > 0 && selectedRows.length === sortedData.length}
                      onChange={toggleSelectAll}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                    />
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort("createdAt")}
                  >
                    <div className="flex items-center gap-1">
                      {t('date')}
                      <ArrowDownUp size={14} className={sortConfig.key === "createdAt" ? "opacity-100" : "opacity-40"} />
                    </div>
                  </th>
                  {(activeTab === "cards" || activeTab === "recharge") && (
                    <>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('user')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('admin')}
                      </th>
                    </>
                  )}
                  
                  {/* Token tracking tab */}
                  {activeTab === "tokens" && (
                    <>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('player')}
                      </th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort("machine")}
                      >
                        <div className="flex items-center gap-1">
                          {t('machineType')}
                          <ArrowDownUp size={14} className={sortConfig.key === "machine" ? "opacity-100" : "opacity-40"} />
                        </div>
                      </th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort("tokens")}
                      >
                        <div className="flex items-center gap-1">
                          {t('tokens')}
                          <ArrowDownUp size={14} className={sortConfig.key === "tokens" ? "opacity-100" : "opacity-40"} />
                        </div>
                      </th>
                    </>
                  )}
                  
                  {/* Card-specific columns */}
                  {activeTab === "cards" && (
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort("action")}
                    >
                      <div className="flex items-center gap-1">
                        {t('action')}
                        <ArrowDownUp size={14} className={sortConfig.key === "action" ? "opacity-100" : "opacity-40"} />
                      </div>
                    </th>
                  )}
                  
                  {/* Recharge-specific columns */}
                  {activeTab === "recharge" && (
                    <>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort("tokens")}
                      >
                        <div className="flex items-center gap-1">
                          {t('tokens')}
                          <ArrowDownUp size={14} className={sortConfig.key === "tokens" ? "opacity-100" : "opacity-40"} />
                        </div>
                      </th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort("paidAmount")}
                      >
                        <div className="flex items-center gap-1">
                          {t('amount')}
                          <ArrowDownUp size={14} className={sortConfig.key === "paidAmount" ? "opacity-100" : "opacity-40"} />
                        </div>
                      </th>
                    </>
                  )}
                  
                  {/* Common column for status */}
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort("isCleared")}
                  >
                    <div className="flex items-center gap-1">
                      {t('status')}
                      <ArrowDownUp size={14} className={sortConfig.key === "isCleared" ? "opacity-100" : "opacity-40"} />
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedData.map((item, index) => {
                  const rowId = getRowId(item, index);
                  return (
                    <tr key={rowId} className="hover:bg-gray-50">
                      <td className="px-4 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={isRowSelected(rowId)}
                          onChange={() => toggleRowSelection(rowId)}
                          className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(item.createdAt)}
                      </td>

                      {(activeTab === "cards" || activeTab === "recharge") && (
                        <>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-8 w-8 bg-gray-100 rounded-full flex items-center justify-center">
                                <User size={16} className="text-gray-500" />
                              </div>
                              <div className="ml-3">
                                <div className="text-sm font-medium text-gray-900">
                                  {item.users?.user_name || t('notAvailable')}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {item.users?.user_email || t('notAvailable')}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {item.users?.user_card || t('notAvailable')}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{item.mediators?.user_name || "N/A"}</div>
                            <div className="text-xs text-gray-500">{item.mediators?.user_email || "N/A"}</div>
                          </td>
                        </>
                      )}

                      {activeTab === "tokens" && (
                        <>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-8 w-8 bg-gray-100 rounded-full flex items-center justify-center">
                                <User size={16} className="text-gray-500" />
                              </div>
                              <div className="ml-3">
                                <div className="text-sm font-medium text-gray-900">
                                  {item.player?.user_name || t('notAvailable')}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {item.player?.user_email || t('notAvailable')}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {item.player?.user_card || t('notAvailable')}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{item.machine?.type || "N/A"}</div>
                          </td>
                        </>
                      )}

                      {activeTab === "cards" && (
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            item.action === "New"
                              ? "bg-emerald-100 text-emerald-800"
                              : "bg-amber-100 text-amber-800"
                          }`}>
                            {item.action === "New" ? t('newCard') : t('replacedCard')}
                          </span>
                        </td>
                      )}

                      {activeTab === "recharge" && (
                        <>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-semibold text-blue-600 flex items-center">
                              <Coins size={14} className="mr-1" />
                              {item.tokens || 0}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-semibold text-green-600 flex items-center">
                              <DollarSign size={14} className="mr-0.5" />
                              {parseFloat(item.paidAmount || 0).toFixed(2)}
                            </div>
                          </td>
                        </>
                      )}

                      {activeTab === "tokens" && (
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-semibold text-blue-600 flex items-center">
                            <Coins size={14} className="mr-1" />
                            {item.tokens || 0}
                          </div>
                        </td>
                      )}

                      <td className="px-6 py-4 whitespace-nowrap">
                        <StatusBadge isCleared={item.isCleared} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}